import React from 'react';
import { CalendarEvent, EVENT_TEMPLATES } from '../types/calendar';
import { format } from 'date-fns';
import { X, Edit, Trash2, Calendar, Clock, MapPin, User, Tag, Link, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import './EventModal.css';

interface EventModalProps {
    event: CalendarEvent;
    onClose: () => void;
    onEdit: (event: CalendarEvent) => void;
    onDelete: (id: string) => void;
    onAddToOutlook: (event: CalendarEvent) => void;
    canEdit: boolean;
}

export const EventModal: React.FC<EventModalProps> = ({
    event,
    onClose,
    onEdit,
    onDelete,
    onAddToOutlook,
    canEdit,
}) => {
    const { isBypassMode } = useAuth();
    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const formatDate = (date: Date) => {
        return format(date, 'PPP'); // e.g., "April 29th, 2021"
    };

    const formatTime = (date: Date) => {
        return format(date, 'p'); // e.g., "5:00 PM"
    };


    return (
        <div className="modal-overlay" onClick={handleBackdropClick}>
            <div className="modal-content">
                <div className="modal-header">
                    <div className="event-header">
                        {event.eventType && (
                            <div className="event-type-badge">
                                {EVENT_TEMPLATES.find(t => t.id === event.eventType)?.name || event.eventType}
                            </div>
                        )}
                    </div>
                    <button className="modal-close-btn" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="modal-body">
                    <h2 className="event-title">{event.title}</h2>

                    <div className="event-details">
                        {event.eventType && (
                            <div className="event-detail">
                                <Tag size={16} />
                                <span>
                                    {EVENT_TEMPLATES.find(t => t.id === event.eventType)?.name || event.eventType}
                                </span>
                            </div>
                        )}

                        <div className="event-detail">
                            <Calendar size={16} />
                            <span>
                                {event.allDay ? (
                                    // Check if it's a single day or date range
                                    formatDate(event.start) === formatDate(event.end)
                                        ? `${formatDate(event.start)}`
                                        : `${formatDate(event.start)} - ${formatDate(event.end)}`
                                ) : (
                                    `${formatDate(event.start)}`
                                )}
                            </span>
                        </div>

                        {!event.allDay && (
                            <div className="event-detail">
                                <Clock size={16} />
                                <span>
                                    {formatTime(event.start)} - {formatTime(event.end)}
                                </span>
                            </div>
                        )}

                        {event.location && (
                            <div className="event-detail">
                                <MapPin size={16} />
                                <span>{event.location}</span>
                            </div>
                        )}

                        

                        {event.isSynced ? (
                            <div className="event-detail sync-info">
                                <Link size={16} />
                                <span>
                                    Synced from {event.syncSource || 'External Source'}
                                    {event.isSynced && (
                                        <span className="read-only-indicator">
                                            <Lock size={12} style={{ marginLeft: '4px' }} />
                                            Read-only
                                        </span>
                                    )}
                                </span>
                            </div>
                        ) : (
                            <div className="event-detail">
                                <User size={16} />
                                <span>Created by {event.createdBy}</span>
                            </div>
                        )}
                    </div>

                    {event.description && (
                        <div className="event-description">
                            <h3>Description</h3>
                            {event.description.startsWith('<html>') ? (
                                <div className="event-description-html" dangerouslySetInnerHTML={{ __html: event.description }} />
                            ) : (
                                <p className="event-description-text">{event.description}</p>
                            )}
                        </div>
                    )}

                    {/* Template-specific fields display */}
                    {event.templateFields && Object.keys(event.templateFields).filter(key => key !== 'calendarSource' && key !== 'originalEventId').length > 0 && (
                        <div className="event-template-fields">
                            <h3>Additional Information</h3>
                            <div className="template-field-list">  
                                {event.templateFields.version && (
                                    <div className="template-field">
                                        <strong>Version:</strong>
                                        <span>{event.templateFields.version}</span>
                                    </div>
                                )}
                                {event.templateFields.releaseType && (
                                    <div className="template-field">
                                        <strong>Release Type:</strong>
                                        <span>{event.templateFields.releaseType}</span>
                                    </div>
                                )}
                                {event.templateFields.institution && (
                                    <div className="template-field">
                                        <strong>Institution:</strong>
                                        <span>{event.templateFields.institution}</span>
                                    </div>
                                )}
                                {event.templateFields.institutionPhase && (
                                    <div className="template-field">
                                        <strong>Phase:</strong>
                                        <span>{event.templateFields.institutionPhase.replace('_', ' ')}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {event.attendees && event.attendees.length > 0 && (
                        <div className="event-attendees">
                            <h3>Attendees</h3>
                            <ul>
                                {event.attendees.map((attendee, index) => (
                                    <li key={index}>{attendee}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    <button
                        className="btn btn-primary"
                        onClick={() => onAddToOutlook(event)}
                        disabled={isBypassMode}
                        title={isBypassMode ? "Personal calendar functionality is disabled in demo mode" : "Add to your Outlook calendar"}
                    >
                        <Calendar size={16} />
                        Add to Outlook
                    </button>

                    {canEdit && !event.isSynced && (
                        <>
                            <button
                                className="btn btn-secondary"
                                onClick={() => onEdit(event)}
                            >
                                <Edit size={16} />
                                Edit
                            </button>

                            <button
                                className="btn btn-danger"
                                onClick={() => {
                                    if (window.confirm('Are you sure you want to delete this event?')) {
                                        onDelete(event.id);
                                    }
                                }}
                            >
                                <Trash2 size={16} />
                                Delete
                            </button>
                        </>
                    )}

                    {event.isSynced && (
                        <div className="sync-warning">
                            <Lock size={16} />
                            <span>This event is synced from {event.syncSource} and cannot be edited manually.</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
